import { useEffect } from 'react';
import { Brand, EmptyState, Footer } from './components/Brand';
import { DepartureSweep } from './components/DepartureSweep';
import { IconClear, IconCrosshair } from './components/Icons';
import { InfoSheet } from './components/InfoSheet';
import { MapControls } from './components/MapControls';
import { MapView } from './components/MapView';
import { NavigationHUD } from './components/NavigationHUD';
import { RouteSummary } from './components/RouteSummary';
import { SearchCard } from './components/SearchCard';
import { ShadeControl } from './components/ShadeControl';
import { Sheet } from './components/Sheet';
import { TimeControl } from './components/TimeControl';
import { Toast } from './components/Toast';
import { WeatherCard } from './components/WeatherCard';
import { reverseLabel } from './lib/geocode';
import { decodePermalink, encodePermalink } from './lib/permalink';
import { useAppStore } from './state/store';
import { useIsDesktop } from './state/useMedia';
import { useNavigation } from './state/useNavigation';
import './styles/app.css';

/** Banner «tocca la mappa» mentre si sceglie un punto. */
function PickBanner() {
  const pickTarget = useAppStore((s) => s.pickTarget);
  const setPickTarget = useAppStore((s) => s.setPickTarget);
  if (!pickTarget) return null;
  return (
    <div className="pick-banner material" role="status">
      <IconCrosshair size={16} />
      <span>Tocca la mappa per scegliere {pickTarget === 'a' ? 'la partenza' : 'l’arrivo'}</span>
      <button
        type="button"
        className="icon-btn"
        aria-label="Annulla"
        onClick={() => setPickTarget(null)}
      >
        <IconClear size={16} />
      </button>
    </div>
  );
}

export function App() {
  const a = useAppStore((s) => s.a);
  const b = useAppStore((s) => s.b);
  const status = useAppStore((s) => s.status);
  const departNow = useAppStore((s) => s.departNow);
  const departMs = useAppStore((s) => s.departMs);
  const mode = useAppStore((s) => s.mode);
  const sliderPct = useAppStore((s) => s.sliderPct);
  const navActive = useAppStore((s) => s.navActive);
  const compute = useAppStore((s) => s.compute);
  const desktop = useIsDesktop();

  useNavigation();

  // ripristino dello stato dal permalink (una volta, al mount)
  useEffect(() => {
    const st = decodePermalink(window.location.hash);
    if (!st) return;
    const s = useAppStore.getState();
    if (st.pref === 'auto') s.setMode('auto');
    else if (typeof st.pref === 'number') s.setSlider(st.pref);
    if (st.t) s.setDepartMs(st.t);
    if (st.a && st.b) s.setBoth(st.a, st.b);
    else if (st.a) s.setPoint('a', st.a);
    else if (st.b) s.setPoint('b', st.b);
  }, []);

  // permalink sempre aggiornato (condivisibile)
  useEffect(() => {
    const hash = encodePermalink({
      a: a?.ll,
      b: b?.ll,
      t: departNow ? undefined : departMs,
      pref: mode === 'auto' ? 'auto' : sliderPct,
    });
    history.replaceState(null, '', hash || window.location.pathname + window.location.search);
  }, [a, b, departNow, departMs, mode, sliderPct]);

  // etichette leggibili per i punti scelti su mappa/GPS (reverse geocoding)
  useEffect(() => {
    for (const which of ['a', 'b'] as const) {
      const p = which === 'a' ? a : b;
      if (!p?.needsReverse) continue;
      const captured = p.ll;
      void reverseLabel(captured).then((label) => {
        const s = useAppStore.getState();
        const current = s[which];
        if (!current || current.ll.lat !== captured.lat || current.ll.lng !== captured.lng) return;
        s.setPointLabel(which, label ?? current.label);
      });
    }
  }, [a, b]);

  // ricalcolo automatico (con debounce) quando cambia un input rilevante;
  // le chiavi coordinate evitano ricalcoli per soli cambi di etichetta
  const aKey = a ? `${a.ll.lat},${a.ll.lng}` : '';
  const bKey = b ? `${b.ll.lat},${b.ll.lng}` : '';
  useEffect(() => {
    if (!aKey || !bKey) return;
    const t = setTimeout(() => {
      void compute();
    }, 450);
    return () => clearTimeout(t);
  }, [aKey, bKey, departNow, departMs, mode, sliderPct, compute]);

  const showEmpty = status === 'idle' && (!a || !b);

  return (
    <div className="app">
      <MapView />
      {!desktop && !navActive && (
        <div className="top-card material">
          <SearchCard />
        </div>
      )}
      <MapControls />
      <PickBanner />

      <Sheet>
        {navActive ? (
          <NavigationHUD />
        ) : (
          <div className="stack">
            {desktop && <Brand />}
            {desktop && <SearchCard />}
            {showEmpty ? <EmptyState /> : <RouteSummary />}
            <div className="card controls">
              <TimeControl />
              <div className="hairline" />
              <ShadeControl />
            </div>
            {status === 'done' && <WeatherCard />}
            {status === 'done' && <DepartureSweep />}
            <Footer />
          </div>
        )}
      </Sheet>

      <InfoSheet />
      <Toast />
    </div>
  );
}
